/**
 * JPG Academy Design System — Motion Utilities
 *
 * Centralized framer-motion animation presets, variants, and transition
 * configurations that maintain brand-consistent motion language.
 *
 * Design principles:
 *   - Motion should feel purposeful, not decorative
 *   - Warm, classical motion (gentle easing, no bouncing)
 *   - Respect prefers-reduced-motion
 *   - Consistent timing: fast (200ms), normal (300ms), slow (500ms)
 *
 * Usage:
 *   import { fadeSlideUp, stagger } from '@jpg/design-system/utils/motion'
 *
 *   <motion.div {...fadeSlideUp} />
 *   <motion.div variants={stagger.container}>
 *     {items.map(item => (
 *       <motion.div key={item.id} variants={stagger.item} />
 *     ))}
 *   </motion.div>
 */

import type { Variants, Transition } from 'framer-motion'

type MotionPreset = {
	initial: Record<string, string | number>
	animate: Record<string, string | number>
	exit: Record<string, string | number>
	transition: Transition
}

// ─── Transitions ────────────────────────────────────────────────────────────

/** Brand-consistent easing curves */
export const ease = {
	/** Default: smooth, natural */
	default: { ease: [0.4, 0, 0.2, 1] } as Transition,
	/** Enter: gentle deceleration */
	enter: { ease: [0, 0, 0.2, 1] } as Transition,
	/** Exit: gentle acceleration */
	exit: { ease: [0.4, 0, 1, 1] } as Transition,
	/** Spring: subtle, not bouncy */
	spring: { type: 'spring' as const, stiffness: 300, damping: 30 },
	/** Stiff spring: for layout changes */
	stiff: { type: 'spring' as const, stiffness: 400, damping: 35 },
	/** Gentle spring: for overlays and modals */
	gentle: { type: 'spring' as const, stiffness: 200, damping: 25 },
}

/** Duration presets (in seconds) */
export const duration = {
	fast: 0.2,
	normal: 0.3,
	slow: 0.5,
}

// ─── Preset Props ───────────────────────────────────────────────────────────
// These are ready-to-spread onto `motion.*` elements.

/** Type for preset animation prop objects */

/**
 * Fade in with a subtle upward slide.
 * Use for content sections, cards, form groups.
 */
export const fadeSlideUp: MotionPreset = {
	initial: { opacity: 0, y: 8 },
	animate: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: 8 },
	transition: { ...ease.default, duration: duration.normal },
}

/**
 * Fade in with a subtle downward slide.
 * Use for dropdowns, tooltips (bottom-positioned).
 */
export const fadeSlideDown: MotionPreset = {
	initial: { opacity: 0, y: -8 },
	animate: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: -8 },
	transition: { ...ease.default, duration: duration.normal },
}

/**
 * Simple fade. Use for overlays, backdrops.
 */
export const fadeIn: MotionPreset = {
	initial: { opacity: 0 },
	animate: { opacity: 1 },
	exit: { opacity: 0 },
	transition: { ...ease.default, duration: duration.normal },
}

/**
 * Fade + subtle scale in. Use for modals, dialogs.
 */
export const scaleIn: MotionPreset = {
	initial: { opacity: 0, scale: 0.95 },
	animate: { opacity: 1, scale: 1 },
	exit: { opacity: 0, scale: 0.95 },
	transition: { ...ease.gentle },
}

/**
 * Slide in from right. Use for drawers.
 */
export const slideRight: MotionPreset = {
	initial: { x: '100%' },
	animate: { x: 0 },
	exit: { x: '100%' },
	transition: { ...ease.default, duration: duration.slow },
}

/**
 * Slide in from left. Use for left-side drawers.
 */
export const slideLeft: MotionPreset = {
	initial: { x: '-100%' },
	animate: { x: 0 },
	exit: { x: '-100%' },
	transition: { ...ease.default, duration: duration.slow },
}

/**
 * Scale in from the center. Use for tooltips.
 */
export const popIn: MotionPreset = {
	initial: { opacity: 0, scale: 0.9 },
	animate: { opacity: 1, scale: 1 },
	exit: { opacity: 0, scale: 0.9 },
	transition: { ...ease.spring, duration: 0.15 },
}

/**
 * Expand height from 0. Use for collapsible sections, accordion.
 */
export const expandVertical: MotionPreset = {
	initial: { height: 0, opacity: 0 },
	animate: { height: 'auto', opacity: 1 },
	exit: { height: 0, opacity: 0 },
	transition: { ...ease.default, duration: duration.normal },
}

// ─── Variant Sets ───────────────────────────────────────────────────────────

/**
 * Staggered children animation.
 * Parent uses `stagger.container`, children use `stagger.item`.
 *
 * Usage:
 *   <motion.ul variants={stagger.container} initial="hidden" animate="visible">
 *     {items.map(item => (
 *       <motion.li key={item.id} variants={stagger.item}>{item.text}</motion.li>
 *     ))}
 *   </motion.ul>
 */
export const stagger: { container: Variants; item: Variants } = {
	container: {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.05,
				delayChildren: 0.02,
			},
		},
	},
	item: {
		hidden: { opacity: 0, y: 8 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { ...ease.default, duration: duration.normal },
		},
	},
}

/**
 * Staggered list for tables and data rows.
 * Faster stagger, more items.
 */
export const listStagger: { container: Variants; item: Variants } = {
	container: {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.03,
				delayChildren: 0.01,
			},
		},
	},
	item: {
		hidden: { opacity: 0, x: -4 },
		visible: {
			opacity: 1,
			x: 0,
			transition: { ...ease.default, duration: duration.fast },
		},
	},
}

/**
 * Tab content transition — crossfade with subtle slide.
 */
export const tabContent: MotionPreset = {
	initial: { opacity: 0, x: 4 },
	animate: { opacity: 1, x: 0 },
	exit: { opacity: 0, x: -4 },
	transition: { ...ease.default, duration: duration.fast },
}

// ─── Layout Animations ─────────────────────────────────────────────────────

/**
 * Layout animation for reordering (drag-and-drop, sortable lists).
 */
export const layoutTransition = {
	layout: { ...ease.stiff } as Transition,
}

// ─── Reduced Motion ─────────────────────────────────────────────────────────

/**
 * Check if user prefers reduced motion.
 * Use to disable animations for accessibility.
 */
export function prefersReducedMotion(): boolean {
	if (typeof window === 'undefined') return false
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Returns animation props that respect prefers-reduced-motion.
 * When reduced motion is preferred, elements appear instantly.
 */
export function accessibleAnimation<T extends Record<string, unknown>>(
	animation: T,
): T {
	if (prefersReducedMotion()) {
		return {
			...animation,
			initial: { opacity: 1 },
			animate: { opacity: 1 },
			exit: { opacity: 1 },
			transition: { duration: 0 },
		} as T
	}
	return animation
}
